import { getMembershipStatusPresentation } from "./subscriptionDisplayStatus.js";

/**
 * @typedef {object} SubscriptionPickerRow
 * @property {string} id
 * @property {string} productName
 * @property {string} statusLabel
 * @property {string|null} expiryLabel
 * @property {string} idSuffix
 * @property {string} vehicleLabel
 * @property {boolean} selectable
 * @property {string|null} blockedReason
 */

export function formatSubscriptionIdSuffix(subscriptionId) {
  const id = String(subscriptionId || "");
  if (id.length < 6) return id;
  return `…${id.slice(-6).toUpperCase()}`;
}

export function formatExpiryLabel(validUntilDateUTC) {
  if (!validUntilDateUTC) return null;
  const d = new Date(validUntilDateUTC);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Build picker rows from GET v1/subscription/by-rego (includes redemption eligibility when present).
 * @param {Array<object>} subscriptions
 * @returns {SubscriptionPickerRow[]}
 */
export function buildSubscriptionPickerRows(subscriptions) {
  const list = Array.isArray(subscriptions) ? subscriptions : [];
  return list.map((sub) => {
    const hasServerEligibility =
      sub.redemption &&
      typeof sub.redemption.eligible === "boolean";
    const eligible = hasServerEligibility
      ? sub.redemption.eligible === true
      : sub.active === true;
    const blockedReason = hasServerEligibility
      ? sub.redemption.reason ||
        (!sub.redemption.eligible ? "Not redeemable" : null)
      : !sub.active
      ? "Inactive subscription"
      : null;

    const vehicle = sub.vehicle;
    const vehicleLabel =
      vehicle && typeof vehicle === "object"
        ? [vehicle.rego, vehicle.make, vehicle.model].filter(Boolean).join(" · ")
        : vehicle
        ? String(vehicle)
        : "—";

    const presentation = getMembershipStatusPresentation(sub);
    return {
      id: String(sub._id),
      productName: sub.product?.name || "Unknown product",
      statusLabel: presentation.statusLabel,
      expiryLabel: presentation.expirySuffix,
      expiryMuted: presentation.expiryMuted,
      idSuffix: formatSubscriptionIdSuffix(sub._id),
      vehicleLabel,
      selectable: eligible,
      blockedReason: eligible ? null : blockedReason || "Not redeemable",
    };
  });
}

export function pickEligibleSubscriptions(subscriptions) {
  return buildSubscriptionPickerRows(subscriptions).filter((r) => r.selectable);
}
